import React, { useState, useCallback, useMemo, useEffect } from "react";
import Invoke from "../../common/utils/invoke";
import "./BlogDetailPage.scss";

/** Number of related-article cards shown per "page" */
const RELATED_ARTICLES_PAGE_SIZE = 5;

/** Placeholder image when blog has no image */
const PLACEHOLDER_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='340' fill='%23f0f0f0'%3E%3Crect width='600' height='340'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23aaa' font-size='18'%3ENo Image%3C/text%3E%3C/svg%3E";

const BlogDetailPage = ({
  currentBlog,
  allBlogs,
  setAllBlogs,
  setCurrentBlog: _setCurrentBlog,
}) => {
  // ─── Local State ────────────────────────────────────────
  const [sharePopup, setSharePopup] = useState({ open: false, blog: null });
  const [relatedPage, setRelatedPage] = useState(1);

  // ─── Related Articles: same blogNudge, sorted by top views ──
  const allRelatedForNudge = useMemo(() => {
    if (!currentBlog) return [];
    return allBlogs
      .filter(
        (b) =>
          b.id !== currentBlog.id &&
          b.blogNudge &&
          b.blogNudge === currentBlog.blogNudge,
      )
      .sort((a, b) => (b.blogViewCounts || 0) - (a.blogViewCounts || 0));
  }, [allBlogs, currentBlog]);

  const relatedArticles = useMemo(
    () => allRelatedForNudge.slice(0, relatedPage * RELATED_ARTICLES_PAGE_SIZE),
    [allRelatedForNudge, relatedPage],
  );


    // ─── CX Fragment → React DOM move logic ───────────────────
useEffect(() => {
  if (!currentBlog?.id) return;

  const MAP = [
    {
      source: ".blogs-details-related-product-section",
      target: ".blog-detail-related-products",
    },
    {
      source: ".blogs-details-related-searches-section",
      target: ".blog-detail-related-searches",
    },
    {
      source: ".blogs-details-related-disclaimer-section",
      target: ".blog-detail-other-content",
    },
  ];

  let lastSignature = "";

  const moveContent = () => {
    MAP.forEach(({ source, target }) => {
      const sourceEl = document.querySelector(source);
      const targetEl = document.querySelector(target);

      if (!sourceEl || !targetEl) return;

      // 🔐 Detect real content change
      const signature = sourceEl.innerText.trim();
      if (!signature || signature === lastSignature) return;

      lastSignature = signature;

      // 🔥 Reset + move
      targetEl.innerHTML = "";

      Array.from(sourceEl.children).forEach((child) => {
        targetEl.appendChild(child.cloneNode(true));
      });

      sourceEl.style.display = "none";
    });
  };

  // Observe ONLY fragment content, not whole body
  const observers = MAP.map(({ source }) => {
    const el = document.querySelector(source);
    if (!el) return null;

    const obs = new MutationObserver(moveContent);
    obs.observe(el, { childList: true, subtree: true });
    return obs;
  });

  // Initial attempt
  moveContent();

  // CX SPA navigation
  const onNavigate = () => {
    lastSignature = "";
    moveContent();
  };

  if (window.Liferay) {
    Liferay.on("endNavigate", onNavigate);
  }

  return () => {
    observers.forEach((o) => o && o.disconnect());
    if (window.Liferay) {
      Liferay.detach("endNavigate", onNavigate);
    }
  };
}, [currentBlog.id]);



  const hasMoreRelated = relatedArticles.length < allRelatedForNudge.length;

  // ─── Handlers ───────────────────────────────────────────
  /** Increment view count via PATCH and then redirect.
   *  Uses an <a> tag so Ctrl+click / middle-click are handled natively
   *  by the browser (opens background tab without stealing focus).
   *  For a plain click we preventDefault and navigate ourselves.
   */
  const handleReadMore = useCallback(
    async (blog, e) => {
      const newCount = (blog.blogViewCounts || 0) + 1;

      // For normal click, prevent the <a> default and navigate manually
      // For Ctrl/Meta+click, let the browser handle it natively (background tab)
      if (e && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
      }

      try {
        if (blog.id) {
          await Invoke.updateBlogViewCount(blog.id, newCount);
        }

        // Update parent allBlogs state
        setAllBlogs((prev) =>
          prev.map((b) =>
            b.id === blog.id ? { ...b, blogViewCounts: newCount } : b,
          ),
        );
      } catch {
        // still navigate even if count update fails
      }

      // Only navigate in-page for normal clicks (Ctrl+click is already handled by the browser)
      if (e && !e.ctrlKey && !e.metaKey && blog.blogFriendlyURL) {
        window.location.href = blog.blogFriendlyURL;
      }
    },
    [setAllBlogs],
  );

  const handleShareOpen = useCallback((blog) => {
    setSharePopup({ open: true, blog });
  }, []);

  const handleShareClose = useCallback(() => {
    setSharePopup({ open: false, blog: null });
  }, []);

  const handleShareClick = useCallback((url) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  const handleLoadMoreRelated = useCallback(() => {
    setRelatedPage((p) => p + 1);
  }, []);

  // ─── Guard ─────────────────────────────────────────────
  if (!currentBlog) {
    return (
      <div className="blog-detail-page__not-found">
        <p>Blog article not found.</p>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────
  return (
    <div className="blog-detail-page">
      {/* ════════════════════════════════════════════════════
          BANNER SECTION
          ════════════════════════════════════════════════════ */}
      <section className="blog-detail-banner">
        {/* Left – text content */}
        <div className="blog-detail-banner__left">
          {currentBlog.blogNudge && (
            <span className="blog-detail-banner__nudge">
              {currentBlog.blogNudge}
            </span>
          )}

          <div className="blog-detail-banner__dic">
            <h1 className="blog-detail-banner__title semibold h4">
              {currentBlog.blogTitle}
            </h1>

            {/*currentBlog.blogSubTitle && (
              <p className="blog-detail-banner__subtitle regular-body light">
                {currentBlog.blogSubTitle}
              </p>
            )*/} 
          </div>

          <div className="blog-detail-banner__meta">
            <div className="blog-detail-banner__discrip">
              {currentBlog.blogPublishDate && (
                <span className="blog-detail-banner__date regular Secondary-Grey-tint-400">
                  <span className="blog-detail-banner__icon blog-detail-banner__icon--calendar" />
                  {currentBlog.blogPublishDate}
                </span>
              )}

              <span className="blog-detail-banner__views regular Secondary-Grey-tint-400">
                <span className="blog-detail-banner__icon blog-detail-banner__icon--eye" />
                {currentBlog.blogViewCounts || 0} views
              </span>
            </div>

            <button
              className="blog-detail-banner__share-btn"
              onClick={() => handleShareOpen(currentBlog)}
              type="button"
              aria-label="Share"
            >
              <ShareIcon />
            </button>
          </div>
        </div>

        {/* Right – banner image */}
        <div className="blog-detail-banner__right">
          <img
            className="blog-detail-banner__image"
            src={
              currentBlog.blogBannerImageURL ||
              currentBlog.blogCardImageURL ||
              PLACEHOLDER_IMG
            }
            alt={currentBlog.blogTitle || "Blog Banner"}
          />
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          CONTENT AREA (main + sidebar)
          ════════════════════════════════════════════════════ */}
      <div className="blog-detail-content">
        {/* ── Div 1: Blog Description (main column) ── */}
        <main className="blog-detail-content__main">
          {currentBlog.blogDescription ? (
            <div
              className="blog-detail-description"
              dangerouslySetInnerHTML={{ __html: currentBlog.blogDescription }}
            />
          ) : (
            <div className="blog-detail-description">
              <p>
                {currentBlog.blogDescriptionRawText || "No content available."}
              </p>
            </div>
          )}
        </main>

        {/* ── Sidebar ── */}
        <aside className="blog-detail-content__sidebar">
          {/* ── Div 2: Related Articles ── */}
          <div className="blog-detail-related-articles blog-aside-wraper">
            <div className="blog-aside-header">
              <h6 className="blog-detail-related-articles__heading m-0 regular-body medium Secondary-Grey-Shades-950">
                Related Articles
              </h6>
            </div>

            <div className="blog-detail-related-articles__list blog-aside-body">
              {relatedArticles.length === 0 ? (
                <p className="blog-detail-related-articles__empty">
                  No related articles found.
                </p>
              ) : (
                <>
                  {relatedArticles.map((blog, idx) => (
                    <div
                      className="related-article-card"
                      key={`${blog.id}-${idx}`}
                    >
                      <div className="related-article-card__body">
                        <h6 className="related-article-card__title regular-body semibold m-0">
                          {blog.blogTitle}
                        </h6>

                        <p className="related-article-card__subtitle small-body regular">
                          <span className="related-article-card__subtitle--text">
                            {blog.blogSubTitle}{" "}
                          </span>
                          <a
                            className="related-article-card__read-more small-body regular"
                            href={blog.blogFriendlyURL || "#"}
                            onClick={(e) => handleReadMore(blog, e)}
                          >
                            Read More
                          </a>
                        </p>

                        <div className="related-article-card__meta">
                          <div className="related-article-card__discrip">
                            <span className="related-article-card__date text-caption regular Secondary-Grey-tint-400">
                              <span className="related-article-card__icon related-article-card__icon--calendar" />
                              {blog.blogPublishDate}
                            </span>
                            <span className="related-article-card__views text-caption regular Secondary-Grey-tint-400">
                              <span className="related-article-card__icon related-article-card__icon--eye" />
                              {blog.blogViewCounts || 0} views
                            </span>
                          </div>
                          <button
                          className="related-article-card__share-btn"
                          onClick={() => handleShareOpen(blog)}
                          type="button"
                          aria-label="Share"
                        >
                          <ShareIcon />
                        </button>
                        </div>
                        
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {hasMoreRelated && (
              <div className="blog-detail-related-articles__load-more-wrapper">
                <button
                  className="blog-detail-related-articles__load-more"
                  onClick={handleLoadMoreRelated}
                  type="button"
                >
                  Load More
                </button>
              </div>
            )}
          </div>

          {/* ── Div 3: Related Products (placeholder) ── */}
          <div className="blog-detail-related-products">
            {/* Placeholder – content to be implemented */}
          </div>

          {/* ── Div 4: Related Searches (placeholder) ── */}
          <div className="blog-detail-related-searches">
            {/* Placeholder – content to be implemented */}
          </div>

          {/* ── Div 5: Other Content (placeholder) ── */}
          <div className="blog-detail-other-content">
            {/* Placeholder – content to be implemented */}
          </div>
        </aside>
      </div>

      {/* ════════════════════════════════════════════════════
          SHARE POPUP (reused design from landing page)
          ════════════════════════════════════════════════════ */}
      {sharePopup.open && sharePopup.blog && (
        <div className="share-popup__overlay" onClick={handleShareClose}>
          <div className="share-popup" onClick={(e) => e.stopPropagation()}>
            <button
              className="share-popup__close"
              onClick={handleShareClose}
              type="button"
              aria-label="Close"
            >
              &times;
            </button>

            <h4 className="share-popup__title">Share</h4>
            <p className="share-popup__subtitle">
              Choose how you&apos;d like to share this.
            </p>

            <ul className="share-popup__list">
              <li
                className="share-popup__item"
                onClick={() =>
                  handleShareClick(sharePopup.blog.blogFacebookShareURL)
                }
              >
                <span className="share-popup__icon share-popup__icon--facebook" />
                <span>Facebook</span>
              </li>
              <li
                className="share-popup__item"
                onClick={() =>
                  handleShareClick(sharePopup.blog.blogLinkedInShareURL)
                }
              >
                <span className="share-popup__icon share-popup__icon--linkedin" />
                <span>LinkedIn</span>
              </li>
              <li
                className="share-popup__item"
                onClick={() =>
                  handleShareClick(sharePopup.blog.blogInstagramShareURL)
                }
              >
                <span className="share-popup__icon share-popup__icon--instagram" />
                <span>Instagram</span>
              </li>
              <li
                className="share-popup__item"
                onClick={() =>
                  handleShareClick(sharePopup.blog.blogGoogleShareURL)
                }
              >
                <span className="share-popup__icon share-popup__icon--google" />
                <span>Google</span>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Helpers ────────────────────────────────────────────────

/** Simple share (arrow) SVG icon – same as landing page */
const ShareIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#8b1a1a"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

export default BlogDetailPage;